/**
 * Группировка и сортировка в адресе страницы (PROP-394): `?groupby=` и `?orderby=`.
 *
 * Тот же приём, что у фильтра (PROP-391/BUG-392): состояние — это структура данных (`groupby`
 * таблицы `{isActive, groups[]}`, `orderby` `{isActive, sorts[]}`), которую таблица шлёт на
 * сервер. Ссылка кладёт её в адрес; страница читает и применяет ДО первой загрузки.
 *
 * ⚠️ «ТОНКАЯ» ФОРМА. Сервер принимает `entity` как `{value:<id>}` или `{sid}` и оператор как
 * `oper.sid` — и по такой форме ОТБИРАЕТ/СОРТИРУЕТ верно. Но конструктор рисует поле по
 * `entity.title`/`entity.entitysid`, а оператор сортировки — прямо `sort.oper.name`: без имени
 * выпадашка пустая (та же болезнь, что типы аргументов в BUG-392). Поэтому перед показом
 * дорастаем `entity` до полного объекта поля, а `oper` — до имени.
 *
 * ⚠️ Значение приходит СНАРУЖИ (ссылку пишет модель, правит человек, пересылает третий), поэтому
 * разбор не бросает наружу и структура проверяется по форме.
 */

import { decodeJsonParam } from '@/cubus/services/urlFilter'

/** `{isActive?, groups: [...]}` — обязателен массив `groups` (пустой допустим). */
export function isGroupbyShape(value) {
  return !!value && typeof value === 'object' && Array.isArray(value.groups)
}

/** `{isActive?, sorts: [...]}` — обязателен массив `sorts`. */
export function isOrderbyShape(value) {
  return !!value && typeof value === 'object' && Array.isArray(value.sorts)
}

export function decodeGroupby(raw) {
  const parsed = decodeJsonParam(raw)
  return isGroupbyShape(parsed) ? parsed : null
}

export function decodeOrderby(raw) {
  const parsed = decodeJsonParam(raw)
  return isOrderbyShape(parsed) ? parsed : null
}

// Поле объекта по идентификатору или sid. `fields` — форма `objAttrsFull()`:
// `{title, value:<entityId>, datatype, entitysid, parententityid, refobjectid, attributeid}`.
function fieldIndex(fields) {
  const byId = new Map()
  const bySid = new Map()
  const list = fields || []
  list.forEach(f => {
    byId.set(String(f.value), f)
    if (f.entitysid) bySid.set(f.entitysid, f)
  })
  return { byId, bySid }
}

// Ссылку на поле (`{value}` / `{sid}` / уже полную) приводим к полному объекту поля.
function resolveField(ref, idx) {
  if (!ref || typeof ref !== 'object') return null
  if (ref.title) return null // уже полная — не трогаем
  if (ref.value != null && idx.byId.has(String(ref.value))) return idx.byId.get(String(ref.value))
  if (ref.sid && idx.bySid.has(ref.sid)) return idx.bySid.get(ref.sid)
  if (ref.entitysid && idx.bySid.has(ref.entitysid)) return idx.bySid.get(ref.entitysid)
  return null
}

// Имя оператора: без него выпадашка пустая. Локализованное имя подставит конструктор, когда
// догрузит операнды; здесь — разумная заглушка, чтобы кнопка не была пустой до загрузки.
const ORDER_OPER_NAMES = { asc: 'Asc', desc: 'Desc' }
function withOperName(oper, names) {
  if (!oper || !oper.sid || oper.name) return oper
  const name = (names && names[oper.sid]) || (oper.sid.charAt(0).toUpperCase() + oper.sid.slice(1))
  return { ...oper, name }
}

// Дорастание строит НОВЫЕ объекты, а не правит пришедшие: значение из адреса — недоверенное, и
// мутировать его на месте значит завязываться на то, что вызывающий не держит на него ссылок.
function hydrateGroup(g, idx) {
  if (!g || typeof g !== 'object') return g
  const out = { ...g }
  const field = resolveField(out.entity, idx)
  if (field) out.entity = { ...field }
  const resField = resolveField(out.resentity, idx)
  if (resField) out.resentity = { ...resField }
  out.oper = withOperName(out.oper, null)
  return out
}

function hydrateSort(s, idx) {
  if (!s || typeof s !== 'object') return s
  const out = { ...s }
  const field = resolveField(out.entity, idx)
  if (field) out.entity = { ...field }
  out.oper = withOperName(out.oper, ORDER_OPER_NAMES)
  return out
}

export function hydrateGroupby(groupby, fields) {
  if (!isGroupbyShape(groupby)) return groupby
  const idx = fieldIndex(fields)
  return { ...groupby, groups: groupby.groups.map(g => hydrateGroup(g, idx)) }
}

export function hydrateOrderby(orderby, fields) {
  if (!isOrderbyShape(orderby)) return orderby
  const idx = fieldIndex(fields)
  return { ...orderby, sorts: orderby.sorts.map(s => hydrateSort(s, idx)) }
}
